import React, { useState, useEffect, useCallback, lazy, Suspense } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import StatCard from '../components/layout/StatCard';
import { statsApi, productsApi } from '../lib/api';
import { useTheme } from '../contexts/ThemeContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Package, QrCode, Users, ShieldWarning, Scan, MapTrifold } from '@phosphor-icons/react';
import { formatDateTime, getStatusColor } from '../lib/utils';
import IncidentGeoDrilldown from '../components/IncidentGeoDrilldown';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const StolenMap = lazy(() => import('../components/StolenMap'));

export default function DashboardPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [stolenData, setStolenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [geoMapLocations, setGeoMapLocations] = useState(null);
  const [mapFocus, setMapFocus] = useState(null);
  const [mapFocusKey, setMapFocusKey] = useState(0);
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  const handleGeoFilterLocations = useCallback((locations) => {
    setGeoMapLocations(locations);
  }, []);

  const handleFocusLocation = useCallback((loc) => {
    if (loc?.latitude == null || loc?.longitude == null) return;
    setMapFocus({
      ...loc,
      latitude: Number(loc.latitude),
      longitude: Number(loc.longitude),
    });
    setMapFocusKey((k) => k + 1);
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [statsRes, productsRes, stolenRes] = await Promise.all([
        statsApi.dashboard(),
        productsApi.list(),
        statsApi.stolen(),
      ]);
      setStats(statsRes.data);
      setProducts(productsRes.data);
      setStolenData(stolenRes.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Prepare QR by Product data
  const qrByProductData = products.map(p => ({
    name: p.name.length > 15 ? p.name.substring(0, 15) + '...' : p.name,
    fullName: p.name,
    qrCodes: p.qr_count || 0
  })).filter(p => p.qrCodes > 0);

  // Scan distribution uses full org totals (same source as Total Scans / QR Stats)
  const consumerScansCount = stats?.consumer_scans ?? 0;
  const internalScansCount = stats?.internal_scans ?? 0;
  const recentConsumerScans =
    stats?.recent_consumer_scans ||
    stats?.recent_scans?.filter((s) => s.scan_type === 'consumer') ||
    [];

  const scanDistribution = [
    { name: t('dashboard.consumerScans'), value: consumerScansCount, color: '#22C55E' },
    { name: t('dashboard.internalScans'), value: internalScansCount, color: '#F97316' },
  ].filter((d) => d.value > 0);

  const counterfeitCount = stats?.total_counterfeit || 0;
  const stolenCount = stats?.total_stolen || 0;

  const goToStolenSection = () => {
    const el = document.getElementById('dashboard-stolen');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const statCards = [
    {
      title: t('dashboard.totalProducts'),
      value: stats?.total_products || 0,
      icon: Package,
      iconClassName: 'text-brand-500 bg-brand-50 dark:bg-brand-500/10',
      onClick: () => navigate('/products'),
    },
    {
      title: t('dashboard.totalQrCodes'),
      value: stats?.total_qr_codes || 0,
      icon: QrCode,
      iconClassName: 'text-brand-500 bg-brand-50 dark:bg-brand-500/10',
      onClick: () => navigate('/qr-codes'),
    },
    {
      title: t('dashboard.totalScans'),
      value: stats?.total_scans || 0,
      icon: Scan,
      iconClassName: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-500/10',
      onClick: () => navigate('/reports'),
    },
    {
      title: t('dashboard.counterfeitAlerts'),
      value: counterfeitCount,
      icon: ShieldWarning,
      iconClassName: 'text-red-600 bg-red-50 dark:bg-red-500/10',
      onClick: () => navigate('/reports'),
    },
    {
      title: t('dashboard.stolenAlerts'),
      value: stolenCount,
      icon: MapTrifold,
      iconClassName: 'text-brand-500 bg-brand-50 dark:bg-brand-500/10',
      onClick: goToStolenSection,
    },
    {
      title: t('dashboard.totalUsers'),
      value: stats?.total_users || 0,
      icon: Users,
      iconClassName: 'text-blue-600 bg-blue-50 dark:bg-blue-500/10',
      onClick: () => navigate('/users'),
    },
  ];

  const chartColors = {
    grid: isDark ? '#27272a' : '#E5E7EB',
    text: isDark ? '#a1a1aa' : '#6B7280',
    bar: '#F97316',
    tooltip: {
      bg: isDark ? '#18181b' : '#ffffff',
      border: isDark ? '#27272a' : '#E5E7EB',
      text: isDark ? '#fafafa' : '#111827'
    }
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{ backgroundColor: chartColors.tooltip.bg, border: `1px solid ${chartColors.tooltip.border}`, color: chartColors.tooltip.text }} className="p-3 rounded-lg shadow-lg">
          <p className="text-sm font-medium">{payload[0]?.payload?.fullName || label}</p>
          <p className="text-sm text-brand-500">
            {payload[0]?.name}: {payload[0]?.value}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <DashboardLayout>
      <div className="oc-page space-y-8" data-testid="dashboard-page">
        <PageHeader title={t('dashboard.title')} description={t('dashboard.subtitle')} />

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3 lg:grid-cols-6">
          {statCards.map((stat) => (
            <StatCard
              key={stat.title}
              title={stat.title}
              value={stat.value}
              icon={stat.icon}
              iconClassName={stat.iconClassName}
              onClick={stat.onClick}
              loading={loading}
              data-testid={`stat-card-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}
            />
          ))}
        </div>

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* QR Generated by Product */}
          <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-lg font-heading ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{t('dashboard.qrByProduct')}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className={`h-[280px] flex items-center justify-center ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Loading...</div>
              ) : qrByProductData.length === 0 ? (
                <div className={`h-[280px] flex items-center justify-center ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  {t('dashboard.noQrYet')}
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={qrByProductData} layout="vertical" margin={{ left: 10, right: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartColors.grid} />
                    <XAxis type="number" stroke={chartColors.text} tick={{ fill: chartColors.text, fontSize: 12 }} />
                    <YAxis 
                      dataKey="name" 
                      type="category" 
                      stroke={chartColors.text} 
                      tick={{ fill: chartColors.text, fontSize: 11 }} 
                      width={100}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="qrCodes" fill={chartColors.bar} radius={[0, 4, 4, 0]} name="QR Codes" />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Scan Type Distribution */}
          <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-lg font-heading ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{t('dashboard.scanDistribution')}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className={`h-[280px] flex items-center justify-center ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Loading...</div>
              ) : scanDistribution.length === 0 ? (
                <div className={`h-[280px] flex items-center justify-center ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  No scans recorded yet
                </div>
              ) : (
                <div className="flex items-center">
                  <ResponsiveContainer width="60%" height={280}>
                    <PieChart>
                      <Pie
                        data={scanDistribution}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {scanDistribution.map((entry) => (
                          <Cell key={entry.name} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: chartColors.tooltip.bg,
                          border: `1px solid ${chartColors.tooltip.border}`,
                          borderRadius: '6px',
                          color: chartColors.tooltip.text
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="w-[40%] space-y-3">
                    {scanDistribution.map((item) => (
                      <div key={item.name} className="flex items-center gap-3">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <div>
                          <p className={`text-sm font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{item.name}</p>
                          <p className={`text-lg font-semibold ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{item.value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* QR Status Distribution */}
          <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-lg font-heading ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{t('dashboard.qrStatusDist')}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className={`h-40 flex items-center justify-center ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Loading...</div>
              ) : stats?.qr_status_distribution && Object.keys(stats.qr_status_distribution).length > 0 ? (
                <div className="space-y-3">
                  {Object.entries(stats.qr_status_distribution).map(([status, count]) => {
                    const total = Object.values(stats.qr_status_distribution).reduce((a, b) => a + b, 0);
                    const percentage = Math.round((count / total) * 100);
                    const statusKeyMap = {
                      in_transit: 'inTransit',
                      blacklisted: 'blacklisted',
                      decommissioned: 'decommissioned',
                    };
                    const statusKey = statusKeyMap[status] || status;
                    return (
                      <div key={status} className="space-y-1">
                        <div className="flex items-center justify-between">
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusColor(status, isDark)}`}>
                            {t(`qr.${statusKey}`, { defaultValue: status })}
                          </span>
                          <span className={`font-medium ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{count} ({percentage}%)</span>
                        </div>
                        <div className={`w-full rounded-full h-2 ${isDark ? 'bg-zinc-800' : 'bg-zinc-100'}`}>
                          <div 
                            className="h-2 rounded-full bg-brand-500" 
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className={`h-40 flex items-center justify-center ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  {t('dashboard.noQrYet')}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Scans */}
          <Card className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm`}>
            <CardHeader className="pb-3">
              <CardTitle className={`text-lg font-heading ${isDark ? 'text-zinc-100' : 'text-zinc-900'}`}>{t('dashboard.recentScans')}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className={`h-40 flex items-center justify-center ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>Loading...</div>
              ) : recentConsumerScans.length > 0 ? (
                <div className="space-y-3">
                  {recentConsumerScans.slice(0, 5).map((scan) => (
                    <div key={scan.id || scan.qr_id + scan.timestamp} className={`flex items-center justify-between py-2 border-b ${isDark ? 'border-zinc-800' : 'border-zinc-100'} last:border-0`}>
                      <div className="flex items-center gap-3">
                        <div className={`w-2 h-2 rounded-full ${scan.scan_type === 'consumer' ? 'bg-green-500' : 'bg-brand-500'}`} />
                        <div>
                          <p className={`text-sm font-mono ${isDark ? 'text-zinc-300' : 'text-zinc-700'}`}>{scan.qr_id?.substring(0, 8)}...</p>
                          <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                            {t(`reports.${scan.scan_type}`, { defaultValue: `${scan.scan_type} scan` })}
                          </p>
                        </div>
                      </div>
                      <span className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>{formatDateTime(scan.timestamp)}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={`h-40 flex items-center justify-center ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  {t('dashboard.noScansYet')}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Stolen / Suspicious Products Map */}
        <Card id="dashboard-stolen" className={`${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} shadow-sm scroll-mt-6`}>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className={`text-lg font-heading ${isDark ? 'text-zinc-100' : 'text-zinc-900'} flex items-center gap-2`}>
                <MapTrifold className="w-5 h-5 text-brand-500" />
                {t('dashboard.stolenProducts')}
              </CardTitle>
              {stolenData && (
                <div className={`text-sm flex gap-3 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
                  <span>{stolenData.total} {t('dashboard.totalIncidents')}</span>
                  {stolenData.stolen_total != null && (
                    <span className="text-red-500">{stolenData.stolen_total} stolen</span>
                  )}
                  {stolenData.suspicious_total != null && (
                    <span className="text-orange-500">{stolenData.suspicious_total} suspicious</span>
                  )}
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <Suspense fallback={<div className="h-[350px] flex items-center justify-center text-zinc-400">Loading map...</div>}>
              {(geoMapLocations?.length > 0 || stolenData?.locations?.length > 0 || mapFocus) ? (
                <StolenMap
                  locations={
                    mapFocus &&
                    !(geoMapLocations || stolenData?.locations || []).some(
                      (l) =>
                        Math.abs(Number(l.latitude) - Number(mapFocus.latitude)) < 1e-5 &&
                        Math.abs(Number(l.longitude) - Number(mapFocus.longitude)) < 1e-5,
                    )
                      ? [...(geoMapLocations || stolenData?.locations || []), mapFocus]
                      : (geoMapLocations || stolenData?.locations || [])
                  }
                  focusLocation={mapFocus}
                  focusKey={mapFocusKey}
                  className="h-[350px]"
                />
              ) : (
                <div className={`h-[350px] flex flex-col items-center justify-center ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`}>
                  <MapTrifold className="w-12 h-12 mb-3 opacity-30" />
                  <p>No stolen/suspicious incidents with GPS location yet</p>
                  <p className="text-xs mt-1 text-center max-w-md">Consumer scans with location enabled will appear on this map. Drill down below filters the pins.</p>
                </div>
              )}
            </Suspense>

            <div className={`mt-5 border-t pt-5 ${isDark ? 'border-zinc-800' : 'border-slate-200'}`}>
              <IncidentGeoDrilldown
                isDark={isDark}
                onFilterLocations={handleGeoFilterLocations}
                onFocusLocation={handleFocusLocation}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
